import { PlaneLanding, PlaneTakeoff, GitBranch, BarChart3, Monitor } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ScheduleRow } from './ScheduleRow'
import { DayFlowView } from './DayFlowView'
import { GanttView } from './GanttView'
import type { TodayBoardData } from './useTodayBoardData'
import type { ViewMode } from './MovementsArea'
import type { ScheduleRowCallbacks } from './SplitView'

interface BoardTabsProps extends ScheduleRowCallbacks {
  data: TodayBoardData
  isToday: boolean
  viewMode: ViewMode
}

function getDefaultTab(viewMode: ViewMode): string {
  if (viewMode === 'list') return 'arriving'
  if (viewMode === 'dayflow') return 'dayflow'
  return 'gantt'
}

export function BoardTabs({ data, viewMode, onRowClick, onCheckIn, onCheckOut, onRegister, onShowDetails }: BoardTabsProps) {
  const { arriving, departing, movements } = data.schedule

  return (
    <Tabs defaultValue={getDefaultTab(viewMode)} className="flex flex-col h-full min-h-0">
      <TabsList className="shrink-0 w-full justify-start">
        {viewMode === 'list' && (
          <>
            <TabsTrigger value="arriving" className="gap-1.5 text-xs">
              <PlaneLanding className="h-3.5 w-3.5" />
              Arrivals
              <Badge variant="secondary" className="h-4 min-w-4 px-1 text-[9px]">
                {arriving.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="departing" className="gap-1.5 text-xs">
              <PlaneTakeoff className="h-3.5 w-3.5" />
              Departures
              <Badge variant="secondary" className="h-4 min-w-4 px-1 text-[9px]">
                {departing.length}
              </Badge>
            </TabsTrigger>
          </>
        )}

        {viewMode === 'dayflow' && (
          <TabsTrigger value="dayflow" className="gap-1.5 text-xs">
            <GitBranch className="h-3.5 w-3.5" />
            Day Flow
            <Badge variant="secondary" className="h-4 min-w-4 px-1 text-[9px]">
              {movements.length}
            </Badge>
          </TabsTrigger>
        )}

        {viewMode === 'gantt' && (
          <TabsTrigger value="gantt" className="gap-1.5 text-xs">
            <BarChart3 className="h-3.5 w-3.5" />
            Timeline
            <Badge variant="secondary" className="h-4 min-w-4 px-1 text-[9px]">
              {movements.length}
            </Badge>
          </TabsTrigger>
        )}
      </TabsList>

      {/* List mode: arrivals + departures tabs */}
      {viewMode === 'list' && (
        <>
          <TabsContent value="arriving" className="flex-1 min-h-0 mt-3">
            <ScrollArea className="h-full">
              {arriving.length === 0 ? (
                <p className="text-xs text-muted-foreground px-3 py-4">None scheduled</p>
              ) : (
                <div className="space-y-0.5">
                  {arriving.map((item) => (
                    <ScheduleRow key={item.allocationId} item={item} onRowClick={onRowClick} onCheckIn={onCheckIn} onCheckOut={onCheckOut} onRegister={onRegister} />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
          <TabsContent value="departing" className="flex-1 min-h-0 mt-3">
            <ScrollArea className="h-full">
              {departing.length === 0 ? (
                <p className="text-xs text-muted-foreground px-3 py-4">None scheduled</p>
              ) : (
                <div className="space-y-0.5">
                  {departing.map((item) => (
                    <ScheduleRow key={item.allocationId} item={item} onRowClick={onRowClick} onCheckIn={onCheckIn} onCheckOut={onCheckOut} onRegister={onRegister} />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </>
      )}

      {/* Day Flow mode */}
      {viewMode === 'dayflow' && (
        <TabsContent value="dayflow" className="flex-1 min-h-0 mt-3">
          <DayFlowView
            movements={movements}
            onRowClick={onRowClick}
            onCheckIn={onCheckIn}
            onCheckOut={onCheckOut}
            onRegister={onRegister}
            onShowDetails={onShowDetails}
          />
        </TabsContent>
      )}

      {/* Gantt mode — suggest tablet/desktop on mobile */}
      {viewMode === 'gantt' && (
        <TabsContent value="gantt" className="flex-1 min-h-0 mt-3">
          <div className="flex items-center gap-1.5 mb-2 px-2 py-1.5 bg-muted rounded text-xs text-muted-foreground">
            <Monitor className="h-3.5 w-3.5 shrink-0" />
            Timeline view works best on tablet or desktop
          </div>
          <GanttView
            movements={movements}
            onRowClick={onRowClick}
            onCheckIn={onCheckIn}
            onCheckOut={onCheckOut}
          />
        </TabsContent>
      )}
    </Tabs>
  )
}
